/**
 * Avatar URL helpers.
 *
 * The API often returns the same silhouette URL for every user without a photo.
 * We detect those at runtime and fall back to initials instead.
 *
 * Strategy:
 *   • Each unique avatar URL maps to a Set of user IDs that use it.
 *   • A URL seen on 2+ different IDs is treated as a system default, not a real upload.
 *   • The store is shallowReactive so Vue components re-render when a URL's
 *     Set reference is replaced, but we don't pay for deep observation of each Set.
 */

import { shallowReactive } from 'vue'

// ─── Initials ─────────────────────────────────────────────────────────────────

/**
 * Lives here rather than in lib/filters so that CAvatar stays free of the store
 * barrel, which pulls in the router and would close an import cycle back to CAvatar.
 *
 * @param {string|null|undefined} fullName
 * @returns {string}
 */
export function getInitials(fullName) {
  if (!fullName) return ''

  const parts = String(fullName).trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0][0].toUpperCase()

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// ─── Shared-URL detection store ───────────────────────────────────────────────

/** @type {Record<string, Set<string>>}  url → set of userId strings */
const urlUserSets = shallowReactive({})

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Extract the raw avatar URL from any supported user shape.
 * Returns null when the user explicitly has no custom avatar.
 *
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export function getRawAvatarUrl(user) {
  if (!user) return null

  // Explicit flags from the API — bail early, no URL needed
  if (user.has_avatar === false || user.has_custom_avatar === false) return null

  const raw = user.avatar_url ?? user.avatar ?? user.image
  if (typeof raw !== 'string') return null

  const src = raw.trim()
  return src || null
}

/**
 * True when the URL is a known generated placeholder rather than a real upload.
 * Covers Gravatar defaults, common stock-silhouette services, and query-flag patterns.
 *
 * @param {string|null|undefined} src
 * @returns {boolean}
 */
export function isPlaceholderAvatarUrl(src) {
  if (!src) return true

  const lower = src.toLowerCase()

  // Gravatar (any variant — the real hash check is done by isSharedDefaultAvatarUrl)
  if (/gravatar\.com\/avatar/.test(lower)) return true

  // Common placeholder service names / path keywords
  if (
    /default|placeholder|no[-_]?avatar|blank|missing|silhouette|anonymous|mystery|ui-avatars|dicebear|pravatar/.test(
      lower,
    )
  ) return true

  // Gravatar `d=` / `default=` query param requesting a stock image
  if (/[?&](d|default)=(mp|mm|blank|identicon|monsterid|wavatar|retro|robohash)(&|$)/.test(lower)) {
    return true
  }

  return false
}

/**
 * Record that `userId` uses `url` as their avatar.
 * Idempotent — calling with the same pair multiple times is a no-op.
 * Replaces the Set reference (triggering shallowReactive) only when a
 * genuinely new user ID is added for that URL.
 *
 * @param {string} url
 * @param {string|number} userId
 */
export function trackAvatarUrl(url, userId) {
  if (!url || userId == null) return

  const key = String(userId)
  const existing = urlUserSets[url]

  if (existing) {
    // Already tracked this exact pair — skip to avoid a spurious reactive update
    if (existing.has(key)) return
    // Clone → replace reference so shallowReactive notices the change
    urlUserSets[url] = new Set(existing).add(key)
  } else {
    urlUserSets[url] = new Set([key])
  }
}

/**
 * True when the same URL is used by 2 or more distinct user IDs,
 * which means it's a system-level default rather than a per-user upload.
 *
 * Reactive: components reading this inside a computed will re-evaluate
 * automatically once a second user is tracked to the same URL.
 *
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isSharedDefaultAvatarUrl(url) {
  if (!url) return true
  const users = urlUserSets[url]
  // Set.size is O(1) — no Object.keys() allocation on every render
  return !!users && users.size >= 2
}

/**
 * Build a favicon URL from a workspace website so we can show a brand mark
 * when no custom `avatar_url` was uploaded. Uses Google's public favicon
 * endpoint (no API key). Returns null when the website cannot be parsed.
 *
 * @param {string|null|undefined} website
 * @param {number} [size=128]
 * @returns {string|null}
 */
export function faviconUrlFromWebsite(website, size = 128) {
  const raw = String(website ?? '').trim()
  if (!raw) return null

  try {
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
    const host = new URL(href).hostname.replace(/^www\./i, '')
    if (!host || !host.includes('.')) return null
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=${size}`
  } catch {
    return null
  }
}